import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { CompanyId, getPortalContext } from '@/lib/portal';
import { getTechProfile, TechnicianProfile } from '@/lib/tech-api-client';
import { getOfflineEnrollmentQueueCount, processOfflineEnrollmentQueue } from '@/lib/offline-enrollment-queue';

interface TechAuthContextType {
  user: User | null;
  accessToken: string | null;
  technician: TechnicianProfile | null;
  companyId: CompanyId | null;
  isOnline: boolean;
  pendingEnrollmentCount: number;
  pendingEnrollmentSyncing: boolean;
  pendingEnrollmentError: string | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshPendingEnrollmentCount: () => Promise<void>;
  syncPendingEnrollments: () => Promise<void>;
}

const TechAuthContext = createContext<TechAuthContextType | undefined>(undefined);

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

export function TechAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [technician, setTechnician] = useState<TechnicianProfile | null>(null);
  const [companyId, setCompanyId] = useState<CompanyId | null>(null);
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  });
  const [pendingEnrollmentCount, setPendingEnrollmentCount] = useState(0);
  const [pendingEnrollmentSyncing, setPendingEnrollmentSyncing] = useState(false);
  const [pendingEnrollmentError, setPendingEnrollmentError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const syncInProgressRef = useRef(false);

  const isBypassAuth = useMemo(() => {
    return import.meta.env.DEV && !isSupabaseConfigured();
  }, []);

  const loadProfileWithToken = async (token: string) => {
    setLoading(true);
    setError(null);

    try {
      const profile = await getTechProfile(token);
      setTechnician(profile.technician);
      setCompanyId(profile.company_id);
    } catch (e) {
      setTechnician(null);
      setCompanyId(null);
      setError(getErrorMessage(e));
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (isBypassAuth) return;

    const { data, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) throw sessionErr;

    const token = data.session?.access_token;
    if (!token) throw new Error('Missing auth token. Please sign in again.');

    setAccessToken(token);
    await loadProfileWithToken(token);
  };

  const refreshPendingEnrollmentCount = async () => {
    try {
      const count = await getOfflineEnrollmentQueueCount();
      setPendingEnrollmentCount(count);
    } catch {
      setPendingEnrollmentCount(0);
    }
  };

  const syncPendingEnrollments = async () => {
    if (isBypassAuth) return;
    if (!accessToken) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (syncInProgressRef.current) return;

    syncInProgressRef.current = true;
    setPendingEnrollmentSyncing(true);
    setPendingEnrollmentError(null);

    try {
      const result = await processOfflineEnrollmentQueue();
      await refreshPendingEnrollmentCount();

      if (result.error) {
        setPendingEnrollmentError(result.error);
      }
    } finally {
      syncInProgressRef.current = false;
      setPendingEnrollmentSyncing(false);
    }
  };

  useEffect(() => {
    if (isBypassAuth) {
      const portal = getPortalContext();
      const fakeCompanyId = portal.companyId || 'climate';
      const fakeUser = { id: 'dev-tech-user-1', email: 'tech.dev@example.com' } as unknown as User;
      const fakeTechnician: TechnicianProfile = {
        id: fakeUser.id,
        company_id: fakeCompanyId,
        successware_employee_code: null,
        full_name: 'Dev Technician',
        email: 'tech.dev@example.com',
        role: 'manager',
        is_active: true,
      };

      setUser(fakeUser);
      setTechnician(fakeTechnician);
      setCompanyId(fakeCompanyId);
      setAccessToken('dev-token');
      setLoading(false);
      return;
    }

    // Active session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAccessToken(session?.access_token ?? null);

      if (session?.access_token) {
        try {
          await loadProfileWithToken(session.access_token);
        } catch {
          // loadProfileWithToken already set error
        }
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      setAccessToken(session?.access_token ?? null);

      if (session?.access_token) {
        try {
          await loadProfileWithToken(session.access_token);
        } catch {
          // loadProfileWithToken already set error
        }
        return;
      }

      setTechnician(null);
      setCompanyId(null);
      setError(null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      setIsOnline(true);
      refreshPendingEnrollmentCount().catch(() => undefined);
      syncPendingEnrollments().catch(() => undefined);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    setIsOnline(navigator.onLine);
    refreshPendingEnrollmentCount().catch(() => undefined);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isBypassAuth]);

  useEffect(() => {
    if (isBypassAuth) return;
    if (!accessToken) return;
    if (!isOnline) return;
    syncPendingEnrollments().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isOnline, isBypassAuth]);

  const signIn = async (email: string, password: string) => {
    if (isBypassAuth) {
      const portal = getPortalContext();
      const fakeCompanyId = portal.companyId || 'climate';
      const fakeUser = { id: 'dev-tech-user-1', email } as unknown as User;
      const fakeTechnician: TechnicianProfile = {
        id: fakeUser.id,
        company_id: fakeCompanyId,
        successware_employee_code: null,
        full_name: 'Dev Technician',
        email,
        role: 'manager',
        is_active: true,
      };

      setUser(fakeUser);
      setTechnician(fakeTechnician);
      setCompanyId(fakeCompanyId);
      setAccessToken('dev-token');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
      if (signInErr) throw signInErr;

      const token = data.session?.access_token;
      if (!data.user || !token) {
        throw new Error('Sign-in failed: missing session.');
      }

      setUser(data.user);
      setAccessToken(token);
      await loadProfileWithToken(token);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    if (isBypassAuth) {
      setUser(null);
      setAccessToken(null);
      setTechnician(null);
      setCompanyId(null);
      setError(null);
      return;
    }

    const { error: signOutErr } = await supabase.auth.signOut();
    if (signOutErr) throw signOutErr;
  };

  return (
    <TechAuthContext.Provider
      value={{
        user,
        accessToken,
        technician,
        companyId,
        isOnline,
        pendingEnrollmentCount,
        pendingEnrollmentSyncing,
        pendingEnrollmentError,
        loading,
        error,
        signIn,
        signOut,
        refreshProfile,
        refreshPendingEnrollmentCount,
        syncPendingEnrollments,
      }}
    >
      {children}
    </TechAuthContext.Provider>
  );
}

export function useTechAuth() {
  const context = useContext(TechAuthContext);
  if (context === undefined) {
    throw new Error('useTechAuth must be used within a TechAuthProvider');
  }
  return context;
}
